import React, { FC, ReactElement, useState } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import useMedia from 'src/hooks/useMedia';
import { useViewportSize } from 'src/hooks/useViewportSize';
import { usePortalContainer } from 'src/hooks/usePortalContainer';
import { usePortalCleanerContainer } from 'src/hooks/usePortalCleanerContainer';
import useModalStore from 'src/hooks/useModalStore';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import IconButton from '../design-system/IconButton/IconButton';
import CloseIcon from '../../icons/Close';

const SpinnerLoader = dynamic(
  () => import('src/shared-components/Loaders/SpinnerLoader'),
  {
    ssr: false
  }
);

export enum ModalPosition {
  // Can be used if modal should centered in relation to the screen
  AbsoluteCenter = 'AbsoluteCenter',
  RelativeCenter = 'RelativeCenter',
  RelativeBottom = 'RelativeBottom',
  RelativeBottomPageCenter = 'RelativeBottomPageCenter',
  RelativeBottomLeft = 'RelativeBottomLeft',
  RelativeScrollBottomPageCenter = 'RelativeScrollBottomPageCenter'
}

export enum ModalRounding {
  All = 'All',
  Bottom = 'Bottom',
  Top = 'Top'
}

export interface ModalPositionAdjustments {
  top?: number;
  left?: number;
}

interface Props {
  children: ReactElement | ReactElement[];
  withCloseIcon?: boolean;
  withShadowBackground?: boolean;
  title?: string | ReactElement;
  // Provide if you want to override the default header with close icon and optional title
  customHeader?: ReactElement;
  position?: ModalPosition;
  roundingMode?: ModalRounding;
  // Provide if you want to render in a specific container instead of modal-root
  modalContainerId?: string;
  // Provide if ModalPosition.Relative[position]
  triggerElementRef?: React.MutableRefObject<HTMLElement>;
  // Provide values in PX if you want to adjust the position of the modal
  // All adjust properties should be passed if used, e.g. {left: -10, top: 0 } for ModalPosition.RelativeBottom to move modal for 10px to the left
  relativePositionAdjustments?: ModalPositionAdjustments;
  onClose?: () => void;
  childrenContainerClass?: string;
  // rewrite position for ModalPosition AbsoluteCenter
  customPositionClassName?: string;
  modalContainerClassName?: string;
  isLoading?: boolean;
  id: string;
  style?: { [key: string]: string };
  fixedModal?: boolean;
  globalContainerClassName?: string;
}

const Modal: FC<Props> = ({
  children,
  position = ModalPosition.AbsoluteCenter,
  roundingMode = ModalRounding.All,
  withCloseIcon = true,
  withShadowBackground = true,
  title,
  customHeader,
  triggerElementRef,
  modalContainerId = 'modal-root',
  onClose = () => null,
  relativePositionAdjustments = {
    top: 0,
    left: 0
  },
  childrenContainerClass,
  modalContainerClassName,
  customPositionClassName,
  isLoading,
  id,
  style,
  fixedModal = true,
  globalContainerClassName
}) => {
  const { isModalVisible } = useModalStore();
  const isVisible = isModalVisible(id);

  const container = usePortalContainer(modalContainerId);
  const newContainer = usePortalCleanerContainer(container, isVisible, id);

  const { viewportWidth, viewportHeight } = useViewportSize();

  const [modalRef, setModalRef] = useState<HTMLDivElement>(null);
  const [windowRef, setWindowRef] = useState<HTMLDivElement>(null);
  const { m } = useMedia();

  const { pathname } = useRouter();

  if (!newContainer || !isVisible) {
    return null;
  }
  const isMobile = !m;

  const isHomePage = pathname === '/';

  const roundingClassName = cn(
    roundingMode === ModalRounding.All && 'rounded-xl',
    roundingMode === ModalRounding.Top && 'rounded-t-xl',
    roundingMode === ModalRounding.Bottom && 'rounded-b-xl'
  );
  const parentRelativeBounding =
    triggerElementRef?.current?.getBoundingClientRect();

  const windowBounding = windowRef?.getBoundingClientRect();

  const modalBounding = modalRef?.getBoundingClientRect();

  const absolutePositionClassName = cn(
    'z-70 absolute left-1/2 transform -translate-x-1/2',
    // Do not use translate-y if modal is bigger than screen
    modalBounding?.top < 0 ? 'top-0' : 'top-1/2 -translate-y-1/2'
  );

  const positionClassName = cn(
    position === ModalPosition.AbsoluteCenter &&
      (customPositionClassName || absolutePositionClassName),
    // Add here any possible relative positions, e.g. ModalPosition.RelativeTop, ModalPosition.RelativeRight, etc.
    (ModalPosition.RelativeBottom ||
      ModalPosition.RelativeCenter ||
      ModalPosition.RelativeBottomPageCenter) &&
      triggerElementRef &&
      'z-70 absolute max-w-screen'
  );

  const getRelativePositionAdjustments = () => {
    const newPositions = {
      top: 0,
      left: 0
    };
    if (
      !isMobile &&
      triggerElementRef &&
      position === ModalPosition.RelativeBottom
    ) {
      // stick to parent accordingly to the ModalPosition.RelativeBottom meaning
      newPositions.top =
        parentRelativeBounding?.top + parentRelativeBounding?.height;
      newPositions.left = parentRelativeBounding?.left;
    }
    if (
      !isMobile &&
      triggerElementRef &&
      position === ModalPosition.RelativeCenter
    ) {
      // stick to parent accordingly to the ModalPosition.RelativeCenter meaning
      newPositions.top =
        parentRelativeBounding?.top + parentRelativeBounding?.height;
      newPositions.left =
        parentRelativeBounding?.left +
        (parentRelativeBounding?.width - modalBounding?.width) / 2;
    }
    if (
      !isMobile &&
      triggerElementRef &&
      position === ModalPosition.RelativeBottomLeft
    ) {
      newPositions.top =
        parentRelativeBounding?.top + parentRelativeBounding?.height;
      newPositions.left =
        parentRelativeBounding?.left +
        (parentRelativeBounding?.width - modalBounding?.width);
    }
    if (
      !isMobile &&
      triggerElementRef &&
      position === ModalPosition.RelativeBottomPageCenter
    ) {
      // stick to parent accordingly to the ModalPosition.RelativeCenter meaning
      newPositions.top =
        parentRelativeBounding?.top + parentRelativeBounding?.height;
      newPositions.left = (windowBounding?.width - modalBounding?.width) / 2;
    }
    if (
      !isMobile &&
      triggerElementRef &&
      position === ModalPosition.RelativeScrollBottomPageCenter
    ) {
      // calculates the coordinates of the modal relative to the document, not the screen
      newPositions.top =
        parentRelativeBounding?.top +
        parentRelativeBounding?.height +
        window.scrollY;
      newPositions.left = (windowBounding?.width - modalBounding?.width) / 2;
    }
    if (relativePositionAdjustments) {
      newPositions.top += relativePositionAdjustments?.top || 0;
      newPositions.left += relativePositionAdjustments?.left || 0;
    }
    return {
      top: `${newPositions?.top}px`,
      left: `${newPositions?.left}px`
    };
  };

  const element = (
    <div
      className={cn(
        'top-0 left-0 w-screen h-screen z-60',
        fixedModal ? 'fixed' : 'absolute',
        globalContainerClassName
      )}
      style={style}
      ref={(el) => {
        setWindowRef(el);
      }}
    >
      {/* Background shadow container */}
      <div
        className={cn(
          'fixed top-0 left-0 w-screen h-screen z-40 bg-shuttleGray opacity-70',
          { hidden: !withShadowBackground }
        )}
      />
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        className={cn(
          'w-screen h-screen top-0 z-60  overflow-y-auto overscroll-y-auto',
          withShadowBackground
            ? 'fixed overscroll-y-contain'
            : 'overscroll-y-auto'
        )}
        style={{
          width: viewportWidth,
          height: viewportHeight
        }}
        onClick={onClose}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
        <div
          onClick={(event) => {
            // Prevents onClose from being triggered from the parent "scrollable" div
            event.stopPropagation();
          }}
          ref={(el) => {
            setModalRef(el);
          }}
          style={
            // Defining element position for relative position values
            position === ModalPosition.RelativeBottom ||
            position === ModalPosition.RelativeCenter ||
            position === ModalPosition.RelativeBottomPageCenter ||
            position === ModalPosition.RelativeBottomLeft ||
            position === ModalPosition.RelativeScrollBottomPageCenter
              ? getRelativePositionAdjustments()
              : {}
          }
          className={cn(
            !isMobile && roundingClassName,
            !isMobile && positionClassName,
            !isMobile && 'w-auto',
            isMobile &&
              !modalContainerClassName &&
              'z-60 absolute top-0 left-0 rounded-b-xl w-screen sm:mt-0',
            'bg-white h-auto',
            !modalContainerClassName && isMobile && isHomePage && 'mt-[62px]',
            !modalContainerClassName && isMobile && !isHomePage && 'mt-[112px]',
            modalContainerClassName
          )}
        >
          <div className="flex flex-col w-full h-full relative">
            {isLoading && (
              <SpinnerLoader containerClassName="top-0 left-0 z-40" />
            )}
            {customHeader || (
              <div
                className={cn(
                  'flex relative items-center px-5 pt-4',
                  title ? 'justify-between' : 'justify-end'
                )}
              >
                {title && (
                  <div className="flex w-full justify-center items-center">
                    {title}
                  </div>
                )}
                {withCloseIcon && (
                  <div data-cy="modal_close_button">
                    <IconButton
                      onClick={onClose}
                      icon={{
                        alt: 'cancel',
                        component: CloseIcon
                      }}
                      isBg={false}
                      className="relative"
                    />
                  </div>
                )}
              </div>
            )}
            <div
              className={cn(
                !childrenContainerClass &&
                  'relative w-full h-full lm:px-11 px-5 pb-4',
                childrenContainerClass
              )}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  return ReactDOM.createPortal(element, newContainer);
};

export default Modal;
